/**
 * Multi-room composite hotel profile: selection state model (pure helpers).
 */

import { tHotelProfileMulti } from "./hotelProfileMultiRoomI18n";

/**
 * @param {object|null} compositeRow — searchPhrase.compositeHotelBookingRow / _compositeRaw shape
 * @returns {object[]} quotes with package_id, sorted by room_index
 */
export function getOkCompositeQuotesForProfile(compositeRow) {
  const raw = compositeRow?.room_quotes;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw
    .filter(
      (q) =>
        q &&
        q.package_id != null &&
        String(q.package_id).trim() !== "" &&
        (q.status === "ok" || q.status == null)
    )
    .slice()
    .sort((a, b) => Number(a.room_index ?? 0) - Number(b.room_index ?? 0));
}

/**
 * Debug-only: per room_quote why it is excluded from multi-room profile UX (must match getOkCompositeQuotesForProfile).
 * @param {object|null} compositeRow
 * @returns {{ entries: object[], passesFilterCount: number, rawLength: number }}
 */
export function diagnoseCompositeQuotesForProfileDebug(compositeRow) {
  const raw = compositeRow?.room_quotes;
  if (!Array.isArray(raw)) {
    return { entries: [], passesFilterCount: 0, rawLength: 0 };
  }
  const entries = raw.map((q, arrIndex) => {
    if (!q) {
      return {
        arrayIndex: arrIndex,
        excluded: true,
        reasons: ["null_or_empty_quote"],
      };
    }
    const reasons = [];
    if (q.package_id == null || String(q.package_id).trim() === "") {
      reasons.push("missing_or_empty_package_id");
    }
    if (q.status != null && q.status !== "ok") {
      reasons.push(`status_not_ok:${String(q.status)}`);
    }
    return {
      arrayIndex: arrIndex,
      room_index: q.room_index,
      package_id: q.package_id,
      status: q.status,
      excluded: reasons.length > 0,
      reasons,
    };
  });
  const passesFilterCount = entries.filter((e) => !e.excluded).length;
  return { entries, passesFilterCount, rawLength: raw.length };
}

/**
 * @param {object[]} rooms — RoomPlacer room groups
 * @param {string|number|null} packageId — Gemonix package id for active leg
 * @returns {object[]}
 */
export function filterRoomsForPackageId(rooms, packageId) {
  if (!Array.isArray(rooms) || rooms.length === 0) return rooms;
  const pid = packageId == null ? "" : String(packageId);
  if (!pid) return rooms;
  return rooms
    .map((room) => {
      const pkgs = (room.packages || []).filter(
        (p) => p && String(p.packageID) === pid
      );
      if (pkgs.length === 0) return null;
      return { ...room, packages: pkgs };
    })
    .filter(Boolean);
}

/**
 * @param {object} roomRequest
 * @param {function} t — i18n t bound to hotelprofile keys
 * @returns {string}
 */
export function getRoomOccupancyLabel(roomRequest, t) {
  if (!roomRequest || typeof roomRequest !== "object") {
    return tHotelProfileMulti(t, "multiRoomOccupancyUnknown");
  }
  const adults = Number(
    roomRequest.AdultsCount ??
      roomRequest.adults_count ??
      roomRequest.adults ??
      0
  );
  const kidsRaw =
    roomRequest.KidsAges ||
    roomRequest.kids_ages ||
    roomRequest.children_ages ||
    roomRequest.Kids ||
    [];
  const kids = Array.isArray(kidsRaw) ? kidsRaw : [];
  const parts = [];
  if (adults > 0) {
    parts.push(
      tHotelProfileMulti(t, "multiRoomAdultsLabel", { count: adults })
    );
  }
  kids.forEach((age) => {
    if (age == null || age === "") return;
    parts.push(
      tHotelProfileMulti(t, "multiRoomChildAgeLabel", { age: String(age) })
    );
  });
  const infants = Number(
    roomRequest.InfantsCount ?? roomRequest.infants ?? roomRequest.INFANTS ?? 0
  );
  if (infants > 0) {
    parts.push(
      tHotelProfileMulti(t, "multiRoomInfantsLabel", { count: infants })
    );
  }
  if (parts.length === 0) {
    return tHotelProfileMulti(t, "multiRoomOccupancyUnknown");
  }
  return parts.join(
    tHotelProfileMulti(t, "multiRoomOccupancySeparator")
  );
}

/**
 * @param {{ selected_option?: object|null }[]} selections
 */
export function canProceedWithAllRooms(selections) {
  if (!Array.isArray(selections) || selections.length === 0) return false;
  return selections.every((s) => s && s.selected_option != null);
}

/**
 * Whether user may open step `index` (sequential gate: prior rooms must be chosen).
 */
export function canAccessRoomTab(index, selections) {
  if (index <= 0) return true;
  for (let j = 0; j < index; j++) {
    if (!selections[j]?.selected_option) return false;
  }
  return true;
}

/**
 * @param {{ selected_option?: object|null }[]} selections
 * @param {number} activeIndex
 */
export function canGoNext(selections, activeIndex) {
  if (!selections[activeIndex]?.selected_option) return false;
  return activeIndex < selections.length - 1;
}

export function canGoPrev(activeIndex) {
  return activeIndex > 0;
}

/**
 * @param {object[]} quotes from getOkCompositeQuotesForProfile
 */
export function buildEmptyMultiRoomSelections(quotes) {
  return quotes.map((q) => ({
    room_index: Number(q.room_index ?? 0),
    room_request: q.room_request ?? null,
    package_id: q.package_id,
    selected_option: null,
    status: "pending",
  }));
}

/**
 * Normalize searchPhrase.rooms[] entry for profile labels + occupancy matching.
 * @param {object} room — raw room composition from SearchContext
 * @param {number} index
 */
export function normalizeSearchPhraseRoomForWizard(room, index) {
  const adults = Number(
    room?.adults ?? room?.AdultsCount ?? room?.adults_count ?? 2
  );
  let kidsAges =
    room?.kidsAges ?? room?.KidsAges ?? room?.children_ages ?? [];
  if (!Array.isArray(kidsAges)) kidsAges = [];
  const kidsCount = Number(room?.kids ?? room?.KidsCount ?? kidsAges.length);
  while (kidsAges.length < kidsCount) {
    kidsAges.push(2);
  }
  if (kidsAges.length > kidsCount) {
    kidsAges = kidsAges.slice(0, kidsCount);
  }
  const infants = Number(
    room?.infants ?? room?.InfantsCount ?? room?.INFANTS ?? 0
  );
  return {
    room_index: index,
    AdultsCount: adults,
    KidsAges: kidsAges,
    InfantsCount: infants,
  };
}

/**
 * @param {object[]} searchPhraseRooms — searchPhrase.rooms
 */
export function buildEmptyWizardMultiRoomSelections(searchPhraseRooms) {
  const arr = Array.isArray(searchPhraseRooms) ? searchPhraseRooms : [];
  return arr.map((room, idx) => {
    const rr = normalizeSearchPhraseRoomForWizard(room, idx);
    return {
      room_index: idx,
      room_request: {
        AdultsCount: rr.AdultsCount,
        KidsAges: [...rr.KidsAges],
        InfantsCount: rr.InfantsCount,
      },
      package_id: null,
      selected_option: null,
      status: "pending",
    };
  });
}

/** @param {unknown[]} ages */
export function normalizeKidAgesForCompare(ages) {
  if (!Array.isArray(ages)) return [];
  return ages
    .map((a) => {
      const n = Number(a);
      return Number.isFinite(n) ? n : NaN;
    })
    .filter((n) => !Number.isNaN(n))
    .sort((x, y) => x - y);
}

/**
 * @returns {string|null} null if this Gemonix room node satisfies roomRequest occupancy
 */
export function gemonixRoomNodeRejectReason(gem, roomRequest) {
  if (!roomRequest || typeof roomRequest !== "object") return null;
  if (!gem || typeof gem !== "object") return "gem_node_missing";
  const ra = Number(roomRequest.AdultsCount ?? roomRequest.adults ?? 0);
  const ga = Number(gem.AdultsCount ?? 0);
  if (ra !== ga) return `adults_mismatch(request=${ra},node=${ga})`;

  const rkRaw = roomRequest.KidsAges ?? roomRequest.kidsAges ?? [];
  const gkRaw = gem.KidsAges;
  const rk = Array.isArray(rkRaw) ? rkRaw.length : 0;
  const gk = Array.isArray(gkRaw) ? gkRaw.length : 0;
  if (rk !== gk) return `kids_count_mismatch(request=${rk},node=${gk})`;

  const agesR = normalizeKidAgesForCompare(rkRaw);
  const agesG = normalizeKidAgesForCompare(gkRaw);
  if (agesR.length !== agesG.length) return "kids_ages_length_mismatch";
  for (let i = 0; i < agesR.length; i++) {
    if (agesR[i] !== agesG[i]) {
      return `kids_age_mismatch(sorted[${i}] request=${agesR[i]} node=${agesG[i]})`;
    }
  }

  const ri = Number(
    roomRequest.InfantsCount ?? roomRequest.infants ?? roomRequest.INFANTS ?? 0
  );
  const gi = Number(gem.InfantsCount ?? gem.infants ?? 0);
  if (ri !== gi) return `infants_mismatch(request=${ri},node=${gi})`;

  return null;
}

function gemonixRoomNodeMatchesRequest(gem, roomRequest) {
  return gemonixRoomNodeRejectReason(gem, roomRequest) == null;
}

/**
 * @returns {string|null} null if pkg matches roomRequest (at least one Rooms[] node fits)
 */
export function whyPackageDoesNotMatchRoomRequest(pkg, roomRequest) {
  if (!pkg || !roomRequest) return "missing_pkg_or_room_request";
  const rooms = normalizeGemonixPackageRooms(pkg.Rooms);
  if (!rooms.length) {
    return "package_missing_Rooms_array";
  }
  const nodeReasons = [];
  for (let i = 0; i < rooms.length; i++) {
    const r = gemonixRoomNodeRejectReason(rooms[i], roomRequest);
    if (r == null) return null;
    nodeReasons.push(`[${i}]:${r}`);
  }
  return `no_room_node_matched ${nodeReasons.join(" | ")}`;
}

/**
 * Structured occupancy-filter diagnosis for RoomPlacer inputs (must match filterRoomsForRoomRequest).
 * @param {object[]} rooms — room groups with .packages[]
 * @param {object|null} roomRequest
 */
export function diagnoseFilterRoomsForRoomRequest(rooms, roomRequest) {
  if (!Array.isArray(rooms)) {
    return {
      inputRoomGroups: 0,
      outputRoomGroups: 0,
      roomRequest,
      groups: [],
    };
  }
  const groups = rooms.map((room, idx) => {
    const pkgs = room.packages || [];
    const packageDetails = pkgs.map((p) => {
      const match = gemonixPackageMatchesRoomRequest(p, roomRequest);
      return {
        packageID: p?.packageID ?? p?.PackageId ?? null,
        match,
        rejectReason: match ? null : whyPackageDoesNotMatchRoomRequest(p, roomRequest),
      };
    });
    const kept = packageDetails.filter((d) => d.match).length;
    return {
      roomGroupIndex: idx,
      roomNames: room.roomNames,
      packageCount: pkgs.length,
      packagesKeptAfterFilter: kept,
      packageDetails,
    };
  });
  const outputRoomGroups = groups.filter((g) => g.packagesKeptAfterFilter > 0).length;
  return {
    inputRoomGroups: rooms.length,
    outputRoomGroups,
    roomRequest,
    groups,
  };
}

/**
 * Gemonix may return package.Rooms as an array or as {"0": {...}, "1": {...}}.
 * @param {unknown} rooms
 * @returns {object[]}
 */
export function normalizeGemonixPackageRooms(rooms) {
  if (rooms == null) return [];
  if (Array.isArray(rooms)) {
    return rooms.filter((r) => r && typeof r === "object");
  }
  if (typeof rooms === "object") {
    return Object.keys(rooms)
      .map((k) => {
        const n = Number(k);
        const sortKey = Number.isFinite(n) ? n : 10_000;
        return { sortKey, room: rooms[k] };
      })
      .filter((e) => e.room && typeof e.room === "object")
      .sort((a, b) => a.sortKey - b.sortKey)
      .map((e) => e.room);
  }
  return [];
}

export function gemonixPackageMatchesRoomRequest(pkg, roomRequest) {
  if (!pkg || !roomRequest) return false;
  const rooms = normalizeGemonixPackageRooms(pkg.Rooms);
  if (!rooms.length) return false;
  return rooms.some((r) => gemonixRoomNodeMatchesRequest(r, roomRequest));
}

/**
 * Filter RoomPlacer room groups to packages matching the active room_request occupancy.
 * @param {object[]} rooms
 * @param {object|null} roomRequest — { AdultsCount, KidsAges, InfantsCount? }
 */
export function filterRoomsForRoomRequest(rooms, roomRequest) {
  if (!Array.isArray(rooms) || rooms.length === 0) return rooms;
  if (!roomRequest) return rooms;
  return rooms
    .map((room) => {
      const pkgs = (room.packages || []).filter((p) =>
        gemonixPackageMatchesRoomRequest(p, roomRequest)
      );
      if (pkgs.length === 0) return null;
      return { ...room, packages: pkgs };
    })
    .filter(Boolean);
}

/**
 * When the user changes the package for room `roomIndex`, clear later legs so
 * summary / booking context stay consistent (sequential wizard invariant).
 * @param {{ selected_option?: object|null, status?: string, room_index?: number }[]} prev
 * @param {number} roomIndex
 * @param {object} selectedRoom
 * @param {object} selectedPackage
 */
export function applyMultiRoomPackageSelection(
  prev,
  roomIndex,
  selectedRoom,
  selectedPackage
) {
  const next = [...prev];
  if (!next[roomIndex]) return prev;
  next[roomIndex] = {
    ...next[roomIndex],
    selected_option: { selectedRoom, selectedPackage },
    status: "selected",
  };
  for (let j = roomIndex + 1; j < next.length; j++) {
    next[j] = {
      ...next[j],
      selected_option: null,
      status: "pending",
    };
  }
  return next;
}

/**
 * Compact snapshot for QA logs (no circular refs).
 * @param {{ room_index?: number, selected_option?: object|null }[]} selections
 */
export function summarizeWizardSelectionsForLog(selections) {
  if (!Array.isArray(selections)) return [];
  return selections.map((s, i) => ({
    i: s?.room_index ?? i,
    pkg: s?.selected_option?.selectedPackage?.packageID ?? null,
    price: s?.selected_option?.selectedPackage?.finalPrice ?? null,
  }));
}
